import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Badge, Kicker, Lead, PageTitle, Row, RowList } from '@/components/ui/Surface';
import { useBackendCourseStore } from '@/store/backendCourseStore';
import type { CourseModuleDto } from '@/api/miniapp/types';

/**
 * Экран 08 на реальном бэкенде. Программа курса.
 *
 * Модули приходят плоским списком; их порядок задаёт поле `order`, а не
 * позиция в массиве. Собственную нумерацию список не рисует: она расходилась
 * бы с номерами в названиях, потому что модули идут с пропусками.
 *
 * Состояния: загрузка, ошибка с повтором, пусто, список.
 */
export function BackendModulesScreen() {
  const navigate = useNavigate();
  const modules = useBackendCourseStore((state) => state.modules);
  const loading = useBackendCourseStore((state) => state.loadingModules);
  const error = useBackendCourseStore((state) => state.error);
  const loadModules = useBackendCourseStore((state) => state.loadModules);

  useEffect(() => {
    void loadModules();
  }, [loadModules]);

  if (loading && modules.length === 0) {
    return (
      <Screen subtitle="Программа курса" tab="course">
        <PanelSkeleton count={5} />
      </Screen>
    );
  }

  if (error && modules.length === 0) {
    return (
      <Screen subtitle="Программа курса" tab="course">
        <LoadFailure
          title="Не удалось загрузить программу"
          description={error}
          onRetry={() => void loadModules()}
        />
      </Screen>
    );
  }

  if (modules.length === 0) {
    return (
      <Screen subtitle="Программа курса" tab="course">
        <StateView
          icon={<BookOpen className="h-6 w-6" aria-hidden />}
          title="Курс пока не открыт"
          description="Модули появятся, когда откроется доступ к программе."
          primary={
            <Button variant="primary" block onClick={() => void loadModules()}>
              Обновить
            </Button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen subtitle="Программа курса" tab="course">
      <Kicker>Ваш курс</Kicker>
      <PageTitle>Бренд врача</PageTitle>
      <Lead>Выберите модуль, чтобы открыть уроки и задания.</Lead>

      <RowList>
        {modules.map((module) => (
          <div key={module.course_id}>
            <Row
              // Номер модуля уже есть в его названии, а порядковый счётчик
              // списка с ним расходится: модули приходят с пропусками
              title={module.title}
              trailing={statusBadge(module)}
              chevron={!statusBadge(module)}
              onClick={() => navigate(`/course/modules/${module.course_id}`)}
            />
          </div>
        ))}
      </RowList>
    </Screen>
  );
}

function statusBadge(module: CourseModuleDto) {
  if (module.status === 'completed') return <Badge tone="success">Пройден</Badge>;
  if (module.status === 'in_progress') return <Badge>В работе</Badge>;
  // Не начатый модуль — обычное состояние, метка тут только шумела бы
  return undefined;
}
